import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import QuestionnairePage from './QuestionnairePage'
import NavigationTabs, { TabItem } from '../../components/NavigationTabs'
import Icon from '../../components/ui/Icon/Icon'
import { useLayout } from '../../context/LayoutContext'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { getCompanyIdFromToken, getDecodedToken } from '../../utils/tokenHelper'
import { getWithExpiry, setWithExpiry } from '../../utils/sessionStorage'
import { useQuestionnaireManagement } from '../../hooks/useQuestionnaireManagement'
import { useJobManagement } from '../../hooks/useJobManagement'
import { useJobQuestionnaires, CloneAttachContext } from '../../hooks/useJobQuestionnaires'
import Button from '../../components/Button'
import JobsService, { UserData, JobData } from '../../api/JobsService'
import { FormBuilderService } from '../../api/FormBuilderService'

// VI-579 follow-up optimization: cache the Select Job drawer's attached-jobs JobData per
// questionnaireId so repeat opens (and tab switches within the session) skip the form-builder
// + Core API fan-out entirely. 60s TTL — stale entries hydrate immediately AND trigger a
// silent background refresh, so the user never sees a flicker. Optimistic ±1 callbacks from
// useJobQuestionnaires / QuestionnaireAttachmentDrawer keep cache consistent on
// attach/detach within the session.
const DRAWER_ATTACHED_JOBS_CACHE_KEY = 'recruiter:drawerAttachedJobs:v1';
const DRAWER_ATTACHED_JOBS_TTL_MS = 60_000;

type DrawerAttachedJobsCacheEntry = { jobs: JobData[]; cachedAt: number };
type DrawerAttachedJobsCache = Record<string, DrawerAttachedJobsCacheEntry>;

const readDrawerAttachedJobsCache = (): DrawerAttachedJobsCache => {
  try {
    const raw = sessionStorage.getItem(DRAWER_ATTACHED_JOBS_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as DrawerAttachedJobsCache) : {};
  } catch {
    return {};
  }
};

const writeDrawerAttachedJobsCache = (questionnaireId: string, jobs: JobData[]): void => {
  try {
    const current = readDrawerAttachedJobsCache();
    current[questionnaireId] = { jobs, cachedAt: Date.now() };
    sessionStorage.setItem(DRAWER_ATTACHED_JOBS_CACHE_KEY, JSON.stringify(current));
  } catch {
    // sessionStorage may be unavailable (Safari private mode); silently degrade.
  }
};

const getDrawerAttachedJobsCacheEntry = (
  questionnaireId: string
): { entry: DrawerAttachedJobsCacheEntry | null; isStale: boolean } => {
  const all = readDrawerAttachedJobsCache();
  const entry = all[questionnaireId];
  if (!entry) return { entry: null, isStale: false };
  return { entry, isStale: Date.now() - entry.cachedAt > DRAWER_ATTACHED_JOBS_TTL_MS };
};
import useResponsive from '../../hooks/useResponsive'
import InterviewsPage from './InterviewsPage'
import SendInviteModal from '../../components/SendInviteModal'
import SuccessModal from '../../components/SuccessModal'
import { useInterviewManagement } from '../../hooks/useInterviewManagement';
import { useInterviewJobsForDropdown } from '../../hooks/useInterviewJobsForDropdown'
import { getInterviewsByCompanyId } from '../../api/ApplicationService'
import Spinner from '../../components/Spinner'
import { isInviteApplicantFeatureEnabled, isJobCentricFeatureEnabled } from '../../utils/featureFlags'
import JobsPage from './JobsPage'
import QuestionnaireCreateOverlay from '../../components/questionnaires/QuestionnaireCreateOverlay'
import DefaultQuestionnaireBanner from '../../components/questionnaires/DefaultQuestionnaireBanner'
import { IQuestionnaireTemplate } from '../../types/questionnaire'
import QuestionnaireSuccessModal from '../../components/QuestionnaireSuccessModal'
import SetDefaultModal from '../../components/questionnaires/default-questionnaire-modals/SetDefaultModal'

const INTERVIEW_FEATURE_ENABLED = import.meta.env.VITE_INTERVIEW_FEATURE_ENABLED === 'true';
// console.log("INTERVIEW_FEATURE_ENABLED",INTERVIEW_FEATURE_ENABLED)


// Available page size options
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;

const getTabIndex = (tabId: string): number => {
  const availableTabs: string[] = [];
  if (INTERVIEW_FEATURE_ENABLED) availableTabs.push('interviews');
  availableTabs.push('questionnaires');
  if (isJobCentricFeatureEnabled()) availableTabs.push('jobs');
  return Math.max(0, availableTabs.indexOf(tabId));
};

const Home = () => {
  const { setHeaderContent } = useLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const decodedToken = getDecodedToken();
  const companyId = useMemo(() => {
    const id = decodedToken?.companyId || 1 || parseInt(getWithExpiry("companyId"));
    console.log('Company ID:', id); // Only log when it actually changes
    return id;
  }, [decodedToken?.companyId]);
  const userId = decodedToken?.userId || null;
  const isMobile = useResponsive('md');

  // Lift all state to Home level
  const [questionnaireCurrentPage, setQuestionnaireCurrentPage] = useState(1);
  const [jobCurrentPage, setJobCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<string | null>(null);
  const [showCreateQuestionnaireModal, setShowCreateQuestionnaireModal] = useState(false);

  //  Track which tabs have been loaded (lazy loading)
  const [loadedTabs, setLoadedTabs] = useState<Set<string>>(
    new Set([INTERVIEW_FEATURE_ENABLED ? 'interviews' : 'questionnaires'])
  );
  const [activeTab, setActiveTab] = useState(INTERVIEW_FEATURE_ENABLED ? 'interviews' : 'questionnaires');
  const [activeTabIndex, setActiveTabIndex] = useState<number | undefined>(undefined);
  const [initialJobId, setInitialJobId] = useState<number | null>(null);
  const [initialJobIdSource, setInitialJobIdSource] = useState<'url' | 'interview-click' | null>(null);
  // VI-623: company-wide completed response total for the "Responses (X)" tab
  // label, reported up from InterviewsPage (scoped to its selected date range).
  const [responseCount, setResponseCount] = useState<number | null>(null);
  const [showQuestionnaireSuccessModal, setShowQuestionnaireSuccessModal] = useState(false);
  const [createdQuestionnaireId, setCreatedQuestionnaireId] = useState<string | null>(null);
  const [createdQuestionnaireName, setCreatedQuestionnaireName] = useState<string | null>(null);
  const [showSetDefaultFromSuccessModal, setShowSetDefaultFromSuccessModal] = useState(false);
  // One-shot context: when a Clone flow ends with a successful Add-to-Job,
  // the toast should read "{questionnaireName} cloned and attached to jobs."
  // (per VI-278 AC4 / Figma). Stored in React state so it survives StrictMode
  // dev-mode unmount/remount cycles (refs would not).
  const [cloneAttachContext, setCloneAttachContext] = useState<CloneAttachContext | null>(null);
  const clearCloneAttachContext = useCallback(() => setCloneAttachContext(null), []);
  // JT-styled toast shown after a Clone → Add-to-Job flow completes.
  // Reuses the same `.toast-notification` UI used by Table.tsx for set-as-default
  // and reactivate confirmations (Figma-matched design).
  const [cloneAttachToast, setCloneAttachToast] = useState<{ isOpen: boolean; message: string }>(
    { isOpen: false, message: '' }
  );
  const [setDefaultSuccessToast, setSetDefaultSuccessToast] = useState<{ isOpen: boolean; message: string }>(
    { isOpen: false, message: '' }
  );
  const handleCloneAttachSuccess = useCallback((questionnaireName: string) => {
    setCloneAttachToast({
      isOpen: true,
      message: `${questionnaireName} cloned and attached to jobs.`,
    });
  }, []);

  // VI-579 follow-up (Bug 1 / Bug 3): late-bound ref to QuestionnairesSection's count updater.
  // The section registers a real handler in its mount effect; until then this is a no-op.
  // useJobQuestionnaires (Select Job drawer) calls handleActiveJobsCountChange with { delta: ±1 }
  // on attach/detach; the View / Reactivate modal's AttachedJobsList calls it with { absolute: N }
  // after a fresh fetch so the table's cached count snaps to the true number.
  const activeJobsCountUpdaterRef = useRef<
    (questionnaireId: string, change: { delta?: number; absolute?: number }) => void
  >(() => {});
  const handleActiveJobsCountChange = useCallback(
    (questionnaireId: string, change: { delta?: number; absolute?: number }) => {
      activeJobsCountUpdaterRef.current(questionnaireId, change);
    },
    []
  );

  // VI-579 follow-up (Bug 2 + optimization): when the Select Job drawer opens for a
  // questionnaire whose attached jobs live outside the first 50-job apiJobs slice, those
  // jobs are invisible to `eligibleJobs`. Fetch them, merge missing ones into apiJobs,
  // populate jobQuestionnaireMap directly (skipping the redundant useJobQuestionnaires
  // re-scan), and cache the JobData in sessionStorage so repeat opens are instant.
  //
  // Ref tracks which questionnaire we last serviced so re-renders during the drawer-open
  // lifecycle don't refire the fetch.
  const preloadedAttachedJobsForRef = useRef<string | null>(null);

  // Gates the entire Select Job drawer's job list on the cold-path preload finishing, so
  // the drawer never shows partial data while it's being assembled. Cache hits (fresh or
  // stale) skip this state — data is rendered immediately and a stale-cache refresh runs
  // silently. true only when there is no cached entry to render from yet.
  const [isLoadingDrawerAttachedJobs, setIsLoadingDrawerAttachedJobs] = useState(false);
  useEffect(() => {
    if (!cloneAttachToast.isOpen) return;
    const timer = setTimeout(() => {
      setCloneAttachToast(prev => ({ ...prev, isOpen: false }));
    }, 3000);
    return () => clearTimeout(timer);
  }, [cloneAttachToast.isOpen]);

  useEffect(() => {
    if (!setDefaultSuccessToast.isOpen) return;
    const timer = setTimeout(() => {
      setSetDefaultSuccessToast(prev => ({ ...prev, isOpen: false }));
    }, 3000);
    return () => clearTimeout(timer);
  }, [setDefaultSuccessToast.isOpen]);
  
  // Track if we've processed URL params on mount
  const hasProcessedUrlParams = useRef(false);

  // Smart default tab resolution
  const hasResolvedDefaultTab = useRef(false);
  const [isResolvingDefaultTab, setIsResolvingDefaultTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return !params.get('tab');
  });

  const interviewData = useInterviewManagement(INTERVIEW_FEATURE_ENABLED ? companyId : 0);

  const { dropdownJobs, isLoadingDropdownJobs } = useInterviewJobsForDropdown(
    INTERVIEW_FEATURE_ENABLED ? interviewData.interviews : [], 
    INTERVIEW_FEATURE_ENABLED ? companyId : 0
  );

  //  Initialize hooks normally - don't pass conditional parameters
  const questionnaireData = useQuestionnaireManagement(companyId, userId);
  const jobData = useJobManagement();

  // Relationship manager email for the "Easy Apply must be enabled..." tooltip contact.
  // One value per company/session -- fetched once, not per job.
  const [relationshipManagerEmail, setRelationshipManagerEmail] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    JobsService.getRelationshipManagerEmail()
      .then((email) => {
        if (!cancelled) setRelationshipManagerEmail(email);
      })
      .catch(() => {
        // Falls back to the default contact email in the tooltip itself
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleJobsDiscovered = useCallback((jobs: JobData[]) => {
    jobData.mergeJobs(jobs);
  }, [jobData.mergeJobs]);
  
  const jobQuestionnaireData = useJobQuestionnaires(
    jobData.apiJobs,
    selectedQuestionnaireId,
    setSelectedQuestionnaireId,
    cloneAttachContext,
    clearCloneAttachContext,
    handleCloneAttachSuccess,
    handleActiveJobsCountChange,
  );

  // VI-579 follow-up (Bug 2 + optimization): on drawer open for a questionnaire,
  //   1) reset jobCurrentPage so we never open on a stale slice,
  //   2) hydrate apiJobs + jobQuestionnaireMap from sessionStorage cache instantly (no flicker),
  //   3) fetch fresh data only if cache is missing or older than 60s; the refresh runs silently
  //      in the background when cache is stale, and only blocks the drawer (via loading row)
  //      when there's no cache at all,
  //   4) directly populate jobQuestionnaireMap entries for the new jobs via
  //      addKnownJobQuestionnaires so the useJobQuestionnaires scan skips them.
  useEffect(() => {
    if (!jobQuestionnaireData.isOffCanvasOpen || !selectedQuestionnaireId) {
      preloadedAttachedJobsForRef.current = null;
      setIsLoadingDrawerAttachedJobs(false);
      return;
    }

    setJobCurrentPage(1);

    const openKey = `${selectedQuestionnaireId}`;
    if (preloadedAttachedJobsForRef.current === openKey) return;
    preloadedAttachedJobsForRef.current = openKey;

    let cancelled = false;

    // Local helper: merge a set of JobData into apiJobs AND tell the questionnaires hook
    // their links directly, so the scan effect downstream doesn't re-issue
    // checkJobQuestionnaire calls for them.
    const mergeAndRegister = (jobs: JobData[]) => {
      if (jobs.length === 0) return;
      const knownIds = new Set((jobData.apiJobs ?? []).map((j: JobData) => j.id));
      const trulyMissing = jobs.filter((j) => !knownIds.has(j.id));
      if (trulyMissing.length === 0) {
        // All cached jobs already in apiJobs; just register the links to ensure the map
        // is correct in case the user hadn't visited them via the scan path yet.
        jobQuestionnaireData.addKnownJobQuestionnaires(
          jobs.map((j) => ({ jobId: j.id, questionnaireId: selectedQuestionnaireId }))
        );
        return;
      }
      jobQuestionnaireData.addKnownJobQuestionnaires(
        trulyMissing.map((j) => ({ jobId: j.id, questionnaireId: selectedQuestionnaireId }))
      );
      jobData.mergeJobs(trulyMissing);
    };

    // 1) Cache hydration — instant render for the user.
    const { entry: cached, isStale } = getDrawerAttachedJobsCacheEntry(openKey);
    if (cached) {
      mergeAndRegister(cached.jobs);
    }

    // 2) Fetch strategy:
    //    - cold (no cache): we have nothing to render yet → gate the drawer with a spinner
    //      and only flip it off once the preload returns.
    //    - stale cache: we already hydrated above, refresh in the background silently.
    //    - fresh cache: nothing to do.
    const shouldRefresh = !cached || isStale;
    if (!shouldRefresh) return;

    const isColdLoad = !cached;
    if (isColdLoad) setIsLoadingDrawerAttachedJobs(true);

    const refresh = async () => {
      try {
        const attached = await FormBuilderService.getAttachedJobsWithSubmissions(selectedQuestionnaireId);
        if (cancelled) return;

        const knownIds = new Set((jobData.apiJobs ?? []).map((j: JobData) => j.id));
        const missingIds = attached.map((a) => a.jobId).filter((id) => !knownIds.has(id));

        // Need full JobData for any jobId not in apiJobs yet.
        const fetched =
          missingIds.length > 0
            ? await Promise.all(missingIds.map((id) => JobsService.getJobById(id).catch(() => null)))
            : [];
        if (cancelled) return;

        const newlyFetched: JobData[] = fetched.filter(
          (j): j is JobData => j !== null && j.status === 'Open'
        );

        // Build the canonical "attached open jobs" list = newly fetched ∪ jobs already in apiJobs
        // that match the attached set. Use it for both merge AND cache write.
        const apiJobsById = new Map((jobData.apiJobs ?? []).map((j: JobData) => [j.id, j]));
        const allAttachedOpen: JobData[] = attached
          .map((a) => apiJobsById.get(a.jobId) ?? newlyFetched.find((nf) => nf.id === a.jobId))
          .filter((j): j is JobData => j !== undefined && j.status === 'Open');

        writeDrawerAttachedJobsCache(openKey, allAttachedOpen);

        if (newlyFetched.length > 0) {
          mergeAndRegister(newlyFetched);
        }
      } catch (err) {
        console.error('Failed to refresh attached jobs for Select Job drawer:', err);
      } finally {
        if (!cancelled && isColdLoad) setIsLoadingDrawerAttachedJobs(false);
      }
    };

    refresh();
    return () => {
      cancelled = true;
    };
    // jobData.apiJobs / jobData.mergeJobs / jobQuestionnaireData.addKnownJobQuestionnaires are
    // intentionally omitted; we only want to react to drawer-open transitions, not to
    // subsequent apiJobs growth (which would loop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobQuestionnaireData.isOffCanvasOpen, selectedQuestionnaireId]);

  // Cache questionnaires when loaded (only when data changes)
  const prevQuestionnairesRef = useRef<any[]>([]);
  useEffect(() => {
    const current = questionnaireData.questionnaires || [];
    const prev = prevQuestionnairesRef.current;
    
    // Only cache if data actually changed (length or IDs)
    if (current.length > 0 && (
      prev.length !== current.length ||
      prev.length === 0 ||
      current.some((q: any, i: number) => q.questionnaireId !== prev[i]?.questionnaireId)
    )) {
      setWithExpiry('cachedQuestionnaires', current, 5 * 60 * 1000);
      prevQuestionnairesRef.current = current;
    }
  }, [questionnaireData.questionnaires]);

  // Cache jobQuestionnaireMap when loaded (only when data changes)
  const prevJobQuestionnaireMapRef = useRef<Map<any, any>>(new Map());
  useEffect(() => {
    const current = jobQuestionnaireData.jobQuestionnaireMap;
    
    if (current?.size > 0) {
      const prev = prevJobQuestionnaireMapRef.current;
      
      // Only cache if map actually changed
      if (prev.size !== current.size || 
          Array.from(current.keys()).some(key => current.get(key) !== prev.get(key))) {
        const mapObject = Object.fromEntries(current);
        setWithExpiry('cachedJobQuestionnaireMap', mapObject, 5 * 60 * 1000);
        prevJobQuestionnaireMapRef.current = new Map(current);
      }
    }
  }, [jobQuestionnaireData.jobQuestionnaireMap]);

  // Cache jobs when loaded (only when data changes)
  const prevJobsRef = useRef<JobData[]>([]);
  useEffect(() => {
    const current = jobData.apiJobs || [];
    const prev = prevJobsRef.current;
    
    // Only cache if data actually changed (length or IDs)
    if (current.length > 0 && (
      prev.length !== current.length ||
      prev.length === 0 ||
      current.some((job, i) => job.id !== prev[i]?.id)
    )) {
      setWithExpiry('cachedJobs', current, 5 * 60 * 1000);
      prevJobsRef.current = current;
    }
  }, [jobData.apiJobs]);

  // Transform job data for the modal
  const jobsForModal = useMemo(() => {
    return jobData.apiJobs?.map(job => ({
      id: job.id.toString(),
      title: job.title
    })) || []
  }, [jobData.apiJobs])

  // Transform questionnaire data for the modal
  const questionnairesForModal = useMemo(() => {
    return questionnaireData.questionnaires?.map(q => ({
      id: q.questionnaireId,
      name: q.questionnaireName || 'Untitled Questionnaire',
      questions: q.questions?.map(question => question.text || question.questionText || 'Untitled Question') || undefined,
      contentType: q.contentType
    })) || []
  }, [questionnaireData.questionnaires])

  //  Lazy load users data
  const [users, setUsers] = useState<Record<number, UserData>>({});
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)

  //  Load users only when questionnaires tab is first accessed
  const loadUsers = useCallback(async () => {
    if (loadingUsers || Object.keys(users).length > 0) return;
    
    setLoadingUsers(true);
    try {
      const companyId = getCompanyIdFromToken();
      if (!companyId) {
        console.error('No company ID found in token');
        setLoadingUsers(false);
        return;
      }

      const usersList = await JobsService.getUsers(companyId);
      const usersMap = usersList.reduce((acc, user) => {
        acc[user.id] = user;
        return acc;
      }, {} as Record<number, UserData>);
      setUsers(usersMap);
      setWithExpiry('users', usersMap, 30 * 60 * 1000);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  }, [loadingUsers]);

  //  Keep setHeaderContent accessible for both tabs
  useEffect(() => {
    setHeaderContent(
      <div style={{ 
        display: 'flex', 
        gap: isMobile ? '8px' : '12px', 
        alignItems: 'center'
      }}>
        {isInviteApplicantFeatureEnabled() && (
          // Invite Applicant Button
          <Button 
            text={isMobile ? (
              <img 
                src="/svgs/paperplane.svg" 
                alt="Invite Applicant" 
                style={{ 
                  width: '16px', 
                  height: '16px',
                  filter: 'brightness(0) saturate(100%) invert(35%) sepia(89%) saturate(1158%) hue-rotate(191deg) brightness(96%) contrast(87%)'
                }}
              />
            ) : "Invite Applicant"}
            variant="transparent" 
            onClick={handleInviteApplicant} 
            id="btn-invite-applicant"
            style={{
              transition: 'all 0.2s ease',
              minWidth: isMobile ? '44px' : 'auto',
              minHeight: isMobile ? '44px' : 'auto',
              padding: isMobile ? '8px' : undefined
            }}
          />
        )}
        {/* Create Questionnaire Button */}
        <Button 
          text={isMobile ? (
            <div>
              <img
                src="/svgs/plus.svg"
                alt="Plus"
                className="plus-icon"
                style={{
                  width: '1rem',
                  height: '1rem',
                  filter: 'brightness(0) saturate(100%) invert(100%)' // ✅ Makes icon white
                }}
              />
            </div>
          ) : "Create Questionnaire"}
          variant="primary" 
          onClick={handleCreateQuestionnaire} 
          id="create-questionnaire-button"
          style={{
            minWidth: isMobile ? '44px' : 'auto', 
            minHeight: isMobile ? '44px' : 'auto',
            padding: isMobile ? '8px' : undefined
          }}
        />
      </div>
    );
  }, [navigate, companyId, setHeaderContent,isMobile]);

  const handleInviteApplicant = () => {
    // if (!jobData.apiJobs || jobData.apiJobs.length === 0) {
    //   jobData.fetchJobs();
    // }

    console.log('Invite applicant clicked');
    setShowInviteModal(true);
  };

  const handleCreateQuestionnaire = () => {
    // setHeaderContent(null);
    console.log('Creating questionnaire');
    // navigate('questionnaire-builder');
    setShowCreateQuestionnaireModal(true);
  };

  const handleSelectTemplate = (template: IQuestionnaireTemplate | null) => {
    setShowCreateQuestionnaireModal(false);
    
    if (template === null) {
      // Build from scratch - navigate to empty builder
      navigate('questionnaire-builder');
    } else {
      // Template selected - navigate with template data
      navigate('questionnaire-builder', {
        state: {
          isTemplate: true,
          templateData: {
            questionnaireName: template.title,
            contentType: template.contentType.toLowerCase(),
            questions: template.questions
          }
        }
      });
    }
  };

  // Fetch initial data once
  const hasFetchedJobsRef = useRef(false);
  
  useEffect(() => {
    const setCompanyIdFunc = () => {
      if(!getWithExpiry("companyId")) {
        setWithExpiry("companyId", companyId?.toString() || "6444", 1000 * 60 * 60 * 24);
      }
    };
    setCompanyIdFunc();

    // Only fetch jobs once on mount
    if (!hasFetchedJobsRef.current) {
      hasFetchedJobsRef.current = true;
      jobData.fetchJobs();
    }
  }, [jobData]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Debug: Track initialJobId and source changes
  useEffect(() => {
    console.log('🔄 Home state changed:', {
      initialJobId,
      initialJobIdSource,
      activeTab
    });
  }, [initialJobId, initialJobIdSource, activeTab]);

  // Handle questionnaire creation success modal
  useEffect(() => {
    const state = location.state as any;
    if (state?.newQuestionnaireId) {
      console.log('📝 New questionnaire created:', state.newQuestionnaireId);
      setCreatedQuestionnaireId(state.newQuestionnaireId);
      setCreatedQuestionnaireName(state.questionnaireName ?? null);
      setShowQuestionnaireSuccessModal(true);
      // If this creation was triggered by Clone, register a one-shot toast
      // context so the next successful attach fires the clone-specific toast.
      if (state.isCloning && state.questionnaireName) {
        setCloneAttachContext({
          questionnaireId: state.newQuestionnaireId,
          questionnaireName: state.questionnaireName,
        });
      }
      // Clear the state to prevent showing modal again on refresh
      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
  }, [location, navigate]);

  // Handle URL parameters to set initial tab
  useEffect(() => {
    // Only process URL params once on mount
    if (hasProcessedUrlParams.current) {
      console.log('⏭️  Home: Skipping URL param processing (already processed)');
      return;
    }

    const tabParam = searchParams.get('tab');
    const jobIdParam = searchParams.get('job_id');
    
    console.log('🔍 Home: Processing URL params:', { tabParam, jobIdParam });
    
    if (tabParam) {
      // Build the available tabs list (same logic as in tabs useMemo)
      const availableTabs: string[] = [];
      
      if (INTERVIEW_FEATURE_ENABLED) {
        availableTabs.push('interviews');
      }
      availableTabs.push('questionnaires');
      if (isJobCentricFeatureEnabled()) {
        availableTabs.push('jobs');
      }

      // Find the index of the requested tab
      const tabIndex = availableTabs.indexOf(tabParam);
      
      if (tabIndex !== -1) {
        // Tab found - set it as active
        console.log('🏠 Home: Setting activeTab from URL:', tabParam, 'at index:', tabIndex);
        setActiveTab(tabParam);
        setActiveTabIndex(tabIndex);
        
        // Mark tab as loaded so data loads
        if (!loadedTabs.has(tabParam)) {
          setLoadedTabs(prev => new Set(prev).add(tabParam));
        }
        
        // If job_id is provided, set it for pre-selection (works for both interviews and jobs tabs)
        if ((tabParam === 'interviews' || tabParam === 'jobs') && jobIdParam) {
          const jobId = parseInt(jobIdParam, 10);
          if (!isNaN(jobId)) {
            console.log('🔗 Home: Setting initialJobId from URL for', tabParam, 'tab:', jobId, '→ Setting source to "url"');
            setInitialJobId(jobId);
            setInitialJobIdSource('url'); // Explicitly mark as coming from URL
          }
        }

        // Mark as processed BEFORE cleaning URL to prevent re-processing
        hasProcessedUrlParams.current = true;

        // Remove tab and job_id parameters from URL after processing
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('tab');
        newSearchParams.delete('job_id');
        const newSearch = newSearchParams.toString();
        const newUrl = `${window.location.pathname}${newSearch ? '?' + newSearch : ''}`;
        window.history.replaceState({}, '', newUrl);
        console.log('🧹 Cleaned URL parameters: tab and job_id removed');
      }
    }
  }, [searchParams]);

  // Smart default tab: resolve based on account data (questionnaires + completed responses)
  useEffect(() => {
    if (hasResolvedDefaultTab.current) return;
    if (hasProcessedUrlParams.current) {
      setIsResolvingDefaultTab(false);
      hasResolvedDefaultTab.current = true;
      return;
    }
    if (questionnaireData.loading) return;

    const resolveTab = async () => {
      let targetTab: string;

      try {
        if (questionnaireData.questionnaires.length === 0) {
          targetTab = 'questionnaires';
        } else if (INTERVIEW_FEATURE_ENABLED) {
          // VI-753: the list endpoint now returns 200 with total:0 for an empty set
          // (it used to 404). Treat 0 completed as "no interviews" and fall through
          // to the Jobs / Questionnaires logic. The 404 catch below is kept as a
          // harmless safety net in case an older backend is in front of this client.
          let completedTotal = 0;
          try {
            const result = await getInterviewsByCompanyId(
              companyId,
              { page: 1, limit: 1, status: 'Completed' }
            );
            completedTotal = result.total ?? 0;
          } catch (err: any) {
            if (err?.response?.status !== 404) {
              throw err;
            }
          }

          if (completedTotal > 0) {
            targetTab = 'interviews';
          } else if (isJobCentricFeatureEnabled()) {
            targetTab = 'jobs';
          } else {
            targetTab = 'questionnaires';
          }
        } else if (isJobCentricFeatureEnabled()) {
          targetTab = 'jobs';
        } else {
          targetTab = 'questionnaires';
        }
      } catch {
        targetTab = INTERVIEW_FEATURE_ENABLED ? 'interviews' : 'questionnaires';
      }

      const tabIndex = getTabIndex(targetTab);
      setActiveTab(targetTab);
      setActiveTabIndex(tabIndex);
      setLoadedTabs(prev => new Set(prev).add(targetTab));
      setIsResolvingDefaultTab(false);
      hasResolvedDefaultTab.current = true;
    };

    resolveTab();
  }, [questionnaireData.loading, questionnaireData.questionnaires.length, companyId]);

  // Calculate derived data at Home level
  const [isFilteringJobs, setIsFilteringJobs] = useState(false);
  const shouldLoadQuestionnaires = loadedTabs.has('questionnaires');
  
  const eligibleJobs = useMemo(() => {
    if (!jobData.filteredJobs || !jobQuestionnaireData.jobQuestionnaireMap) return [];
    
    return jobData.filteredJobs.filter(job => {
      if (job.status !== 'Open') return false;

      const hasQuestionnaire = jobQuestionnaireData.jobsWithQuestionnaires.has(job.id);
      
      if (hasQuestionnaire) {
        const jobQuestionnaireId = jobQuestionnaireData.jobQuestionnaireMap.get(job.id);
        return jobQuestionnaireId === selectedQuestionnaireId;
      }
      
      return true;
    });
  }, [jobData.filteredJobs, jobQuestionnaireData.jobsWithQuestionnaires, jobQuestionnaireData.jobQuestionnaireMap, selectedQuestionnaireId]);

  // Track filtering state
  useEffect(() => {
    setIsFilteringJobs(jobData.isLoadingJobs || jobQuestionnaireData.isLoadingJobQuestionnaires);
  }, [jobData.isLoadingJobs, jobQuestionnaireData.isLoadingJobQuestionnaires]);

  // Calculate paginated data
  const paginatedQuestionnaires = useMemo(() => {
    const startIndex = (questionnaireCurrentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return questionnaireData.questionnaires.slice(startIndex, endIndex);
  }, [questionnaireData.questionnaires, questionnaireCurrentPage, itemsPerPage]);

  const paginatedJobsEligible = useMemo(() => {
    const startIndex = (jobCurrentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return eligibleJobs.slice(startIndex, endIndex);
  }, [eligibleJobs, jobCurrentPage, itemsPerPage]);

  const openApiJobs = useMemo(() => {
    if (!jobData.apiJobs) return [];
    return jobData.apiJobs.filter((job: JobData) => job.status === 'Open');
  }, [jobData.apiJobs]);

  const totalPages = useMemo(() => {
    return Math.ceil(questionnaireData.questionnaires.length / itemsPerPage);
  }, [questionnaireData.questionnaires.length, itemsPerPage]);

  // Check if any questionnaire is set as default (across all questionnaires, not just current page)
  const hasDefaultQuestionnaire = useMemo(() => {
    return questionnaireData.questionnaires.some((q: any) => q.isDefault === true);
  }, [questionnaireData.questionnaires]);

  // Event handlers
  const handleJobPageChange = (page: number) => {
    setJobCurrentPage(page);
  };

  const handlePageSizeChange = (newSize: number) => {
    setItemsPerPage(newSize);
    setQuestionnaireCurrentPage(1);
    setJobCurrentPage(1);
  };

  const handlePreview = (itemId: string) => {
    console.log(`Previewing questionnaire with ID ${itemId}`);
  };

  const handleAddToJob = (_: number, itemId: string) => {
    jobQuestionnaireData.handleOpenOffCanvas(itemId);
  };

  // Handle questionnaire success modal actions
  const handleCloseQuestionnaireSuccessModal = useCallback(() => {
    setShowQuestionnaireSuccessModal(false);
    setCreatedQuestionnaireId(null);
    setCreatedQuestionnaireName(null);
    // Clear any pending clone-toast context if the user dismisses without attaching.
    setCloneAttachContext(null);
  }, []);

  const handleAddQuestionnaireToJob = useCallback(() => {
    setShowQuestionnaireSuccessModal(false);
    // Open the questionnaires drawer with the created questionnaire
    if (createdQuestionnaireId) {
      jobQuestionnaireData.handleOpenOffCanvas(createdQuestionnaireId);
    }
  }, [createdQuestionnaireId, jobQuestionnaireData]);

  const handleSetAsDefaultFromSuccessModal = useCallback(() => {
    setShowQuestionnaireSuccessModal(false);
    setShowSetDefaultFromSuccessModal(true);
  }, []);

  const handleConfirmSetDefaultFromSuccessModal = useCallback(async () => {
    if (createdQuestionnaireId) {
      await questionnaireData.handleSetDefault(createdQuestionnaireId);
      setSetDefaultSuccessToast({
        isOpen: true,
        message: `${createdQuestionnaireName ?? 'Questionnaire'} has been set as your default questionnaire.`,
      });
    }
    setShowSetDefaultFromSuccessModal(false);
    setCreatedQuestionnaireId(null);
    setCreatedQuestionnaireName(null);
  }, [createdQuestionnaireId, createdQuestionnaireName, questionnaireData]);

  const handleCancelSetDefaultFromSuccessModal = useCallback(() => {
    setShowSetDefaultFromSuccessModal(false);
    setCreatedQuestionnaireId(null);
    setCreatedQuestionnaireName(null);
  }, []);

  //  Lazy load tab data when first accessed
  const handleTabChange = useCallback((activeIndex: number, tabItem: TabItem) => {
    console.log('Tab changed to:', tabItem.label, 'at index:', activeIndex);
    
    const tabId = tabItem.id;
    setActiveTab(tabId);
    setActiveTabIndex(activeIndex); // Update activeTabIndex when user manually switches tabs
    
    // Clear initialJobId based on context:
    // 1. If switching TO interviews and source is 'url', clear it (URL was for Jobs tab, not Interviews)
    // 2. If switching AWAY FROM interviews and source is 'interview-click', clear it
    if (initialJobId !== null) {
      if (tabId === 'interviews' && initialJobIdSource === 'url') {
        console.log('🧹 Clearing initialJobId - URL was for Jobs tab, not Interviews tab');
        setInitialJobId(null);
        setInitialJobIdSource(null);
      } else if (tabId !== 'interviews' && initialJobIdSource === 'interview-click') {
        console.log('🧹 Clearing initialJobId - leaving Interviews tab after interview-click');
        setInitialJobId(null);
        setInitialJobIdSource(null);
      } else if (tabId !== 'interviews' && initialJobIdSource === 'url') {
        console.log('✋ Keeping initialJobId - staying in Jobs tab with URL parameter');
      }
    }
    
    //  Lazy load data for tabs that haven't been loaded yet
    if (!loadedTabs.has(tabId)) {
      console.log(`🚀 Lazy loading data for tab: ${tabId}`);
      
      setLoadedTabs(prev => new Set(prev).add(tabId));
      
      // Load specific data based on tab
      // if (tabId === 'questionnaires') {
      //   loadUsers(); // Load users for "Created by" field
      //   // jobData.fetchJobs();
      // }
      // TODO: Add interview data loading when implemented
      // if (tabId === 'interviews') {
      //   loadInterviewData();
      // }
    }
  }, [loadedTabs, loadUsers, initialJobId, initialJobIdSource]);

  // Handle interview count click from Jobs tab
  const handleInterviewCountClick = useCallback((jobId: number) => {
    console.log('🔗 Interview count clicked for job ID:', jobId);
    
    // Clear initialJobId first to ensure useEffect in InterviewsPage detects the change
    setInitialJobId(null);
    setInitialJobIdSource(null);
    console.log('📊 initialJobId changed to: null');
    
    // Then set the new jobId (small delay to ensure state update)
    setTimeout(() => {
      setInitialJobId(jobId);
      setInitialJobIdSource('interview-click'); // Explicitly mark as coming from interview click
      console.log('📊 initialJobId changed to:', jobId, 'source: interview-click');
      
      // Switch to interviews tab (interviews is always index 0 when enabled)
      if (INTERVIEW_FEATURE_ENABLED) {
        setActiveTab('interviews');
        setActiveTabIndex(0);
        setLoadedTabs(prev => new Set(prev).add('interviews'));
        // VI-623: the clicked job row can be far down the Jobs list, so the
        // window is scrolled down. Reset to the top so the Responses tab opens
        // at the top instead of preserving the Jobs-tab scroll offset.
        window.scrollTo(0, 0);
        console.log(`✅ Switched to interviews tab with job ID: ${jobId}`);
      }
    }, 0);
  }, []);

  //  Define tabs with feature gate
  const tabs: TabItem[] = useMemo(() => {
    const allTabs: TabItem[] = [];

    // Add interviews tab only if feature is enabled
    if (INTERVIEW_FEATURE_ENABLED) {
      allTabs.push({
        id: 'interviews',
        label: responseCount !== null ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.6rem' }}>
            Responses
            {/* VI-623: count rendered as a curved-square primary badge
                (JT platform label style), not a pill/circle. */}
            <span
              className="bg-primary text-light"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '2rem',
                padding: '0.35rem 0rem',
                borderRadius: '6px',
                fontSize: '0.7rem',
                fontWeight: 600,
                lineHeight: 1,
              }}
            >
              {responseCount}
            </span>
          </span>
        ) : 'Responses',
        content: (
          <div>
            <InterviewsPage 
              isMobile={isMobile} 
              jobData={dropdownJobs}
              allJobs={jobData.apiJobs}
              isLoadingJobs={isLoadingDropdownJobs}
              isLoadingAllJobs={jobData.isLoadingJobs}
              interviews={interviewData.interviews}
              isLoadingInterviews={interviewData.isLoading}
              isLoadingMore={interviewData.isLoadingMore}
              isLoadingByJobId={interviewData.isLoadingByJobId}
              interviewError={interviewData.error}
              hasMoreToLoad={interviewData.hasMoreToLoad}
              onLoadMore={interviewData.loadMoreInterviews}
              currentPage={interviewData.currentPage}
              totalPages={interviewData.totalPages}
              hasNextPage={interviewData.hasNextPage}
              hasPreviousPage={interviewData.hasPreviousPage}
              onNextPage={interviewData.loadNextPage}
              onPreviousPage={interviewData.loadPreviousPage}
              onFilterByStatus={interviewData.filterByStatus}
              onRefresh={interviewData.refetchInterviews}
              initialJobId={initialJobId}
              initialJobIdSource={initialJobIdSource}
              onFetchByJobId={interviewData.fetchInterviewsByJobId}
              onRestoreCompanyInterviews={interviewData.restoreCompanyInterviews}
              jobsWithQuestionnaires={jobQuestionnaireData.jobsWithQuestionnaires}
              jobQuestionnaireMap={jobQuestionnaireData.jobQuestionnaireMap}
              jobQuestionnaireDataMap={jobQuestionnaireData.jobQuestionnaireDataMap}
              isLoadingJobQuestionnaires={jobQuestionnaireData.isLoadingJobQuestionnaires}
              onJobsDiscovered={handleJobsDiscovered}
              paginationMeta={jobData.paginationMeta}
              companyId={companyId}
              onResponseCountChange={setResponseCount}
              viewingJobId={interviewData.viewingJobId}
              onDateRangeChange={interviewData.refetchForDateRange}
            />
          </div>
        ),
        panelId: 'panel-interviews'
      });
    }

    // Always add questionnaires tab
    allTabs.push({
      id: 'questionnaires',
      label: 'Questionnaires',
      content: shouldLoadQuestionnaires ? (
        <QuestionnairePage 
          questionnaireData={questionnaireData}
          users={users}
          loadingUsers={loadingUsers}
          jobData={jobData}
          jobQuestionnaireData={jobQuestionnaireData}
          selectedQuestionnaireId={selectedQuestionnaireId}
          setSelectedQuestionnaireId={setSelectedQuestionnaireId}
          paginatedQuestionnaires={paginatedQuestionnaires}
          paginatedJobsEligible={paginatedJobsEligible}
          eligibleJobs={eligibleJobs}
          questionnaireCurrentPage={questionnaireCurrentPage}
          setQuestionnaireCurrentPage={setQuestionnaireCurrentPage}
          jobCurrentPage={jobCurrentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          handlePageSizeChange={handlePageSizeChange}
          handleJobPageChange={handleJobPageChange}
          handlePreview={handlePreview}
          handleAddToJob={handleAddToJob}
          isFilteringJobs={isFilteringJobs}
          PAGE_SIZE_OPTIONS={PAGE_SIZE_OPTIONS}
          openApiJobs={openApiJobs}
          jobQuestionnaireMap={jobQuestionnaireData.jobQuestionnaireMap}
          onSetDefault={questionnaireData.handleSetDefault}
          onRemoveDefault={questionnaireData.handleRemoveDefault}
          hasDefaultQuestionnaire={hasDefaultQuestionnaire}
          activeJobsCountUpdaterRef={activeJobsCountUpdaterRef}
          isLoadingDrawerAttachedJobs={isLoadingDrawerAttachedJobs}
          relationshipManagerEmail={relationshipManagerEmail}
        />
      ) : (
        <div className='d-flex justify-content-center align-items-center' style={{ minHeight: '400px' }}>
          <div className='text-center'>
            <h3>Click to Load Questionnaires</h3>
            <p>Questionnaire data will load when you click this tab</p>
          </div>
        </div>
      ),
      panelId: 'panel-questionnaires'
    });

    if(isJobCentricFeatureEnabled()){
      console.log('🎯 Rendering JobsPage with props:', {
        initialJobId,
        initialJobIdSource,
        activeTab,
        jobsCount: jobData.apiJobs?.length || 0
      });
      
      allTabs.push({
        id: 'jobs',
        label: 'Jobs',
        content: (
          <JobsPage 
            isMobile={isMobile} 
            jobs={jobData.apiJobs || []}
            isLoadingJobs={jobData.isLoadingJobs}
            onRefresh={jobData.fetchJobs}
            questionnaires={questionnaireData.questionnaires}
            jobQuestionnaireMap={jobQuestionnaireData.jobQuestionnaireMap}
            jobsWithQuestionnaires={jobQuestionnaireData.jobsWithQuestionnaires}
            isLoadingQuestionnaires={jobQuestionnaireData.isLoadingJobQuestionnaires}
            loadingJobIds={jobQuestionnaireData.loadingJobIds}
            onRefreshSingleJob={jobQuestionnaireData.refreshSingleJobQuestionnaire}
            initialJobId={initialJobId}
            initialJobIdSource={initialJobIdSource}
            onInterviewCountClick={handleInterviewCountClick}
            activeTab={activeTab}
            onJobsDiscovered={handleJobsDiscovered}
            paginationMeta={jobData.paginationMeta}
            onActiveJobsCountChange={handleActiveJobsCountChange}
            relationshipManagerEmail={relationshipManagerEmail}
          />
        ),
        panelId: 'panel-jobs'
      });
    }
    

    return allTabs;
  }, [
    shouldLoadQuestionnaires, questionnaireData, users, loadingUsers,
    // Only depend on the actual data arrays, not the whole hook objects
    jobData.apiJobs, jobData.isLoadingJobs,
    jobQuestionnaireData.jobQuestionnaireMap, jobQuestionnaireData.jobsWithQuestionnaires, jobQuestionnaireData.isLoadingJobQuestionnaires,
    selectedQuestionnaireId, paginatedQuestionnaires, paginatedJobsEligible,
    eligibleJobs, questionnaireCurrentPage, jobCurrentPage, totalPages, itemsPerPage, 
    handlePageSizeChange, handleJobPageChange, handlePreview, handleAddToJob, isFilteringJobs,
    relationshipManagerEmail,
    // Interview dependencies (only needed if enabled)
    ...(INTERVIEW_FEATURE_ENABLED ? [
      isMobile, dropdownJobs, isLoadingDropdownJobs, interviewData, loadedTabs, initialJobId, initialJobIdSource,
      responseCount, companyId
    ] : [])
  ]);

  // console.log("SHOW SUCCESS MODAL", showSuccessModal)

  return (
    <div>
      {showCreateQuestionnaireModal && (
        <QuestionnaireCreateOverlay 
          isOpen={showCreateQuestionnaireModal}
          onClose={() => setShowCreateQuestionnaireModal(false)}
          onSelectTemplate={handleSelectTemplate}
        />
      )}
      <DefaultQuestionnaireBanner
        show={!hasDefaultQuestionnaire && questionnaireData.questionnaires.length > 0}
        onSetDefault={() => {
          const firstActive = questionnaireData.questionnaires.find((q: any) => q.active);
          if (firstActive) {
            questionnaireData.handleSetDefault(firstActive.questionnaireId);
          }
        }}
      />
      <div style={{ position: 'relative' }}>
        <div style={{ visibility: isResolvingDefaultTab ? 'hidden' : 'visible' }}>
          <NavigationTabs 
            tabs={tabs}
            defaultActiveTab={0}
            activeTab={activeTabIndex}
            onTabChange={handleTabChange}
            className="home-navigation"
          />
        </div>
        {isResolvingDefaultTab && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
            }}
          >
            <Spinner size="xl" />
          </div>
        )}
      </div>

      <SendInviteModal 
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        jobs={jobsForModal}
        questionnaires={questionnairesForModal}
        isLoadingJobs={jobData.isLoadingJobs}
        onSuccess={() => setShowSuccessModal(true)}
      />

      <SuccessModal 
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
      />

      <QuestionnaireSuccessModal
        isOpen={showQuestionnaireSuccessModal}
        onClose={handleCloseQuestionnaireSuccessModal}
        onAddToJob={handleAddQuestionnaireToJob}
        onSetAsDefault={handleSetAsDefaultFromSuccessModal}
      />

      <SetDefaultModal
        isOpen={showSetDefaultFromSuccessModal}
        questionnaireName={createdQuestionnaireName ?? ''}
        onConfirm={handleConfirmSetDefaultFromSuccessModal}
        onCancel={handleCancelSetDefaultFromSuccessModal}
      />

      {/* JT-styled clone-attach success toast (Figma-matched, VI-278 AC4) */}
      {cloneAttachToast.isOpen && (
        <div className="toast-notification-container">
          <div className="toast-notification toast-success">
            <i
              className="fal fa-circle-check"
              style={{ fontSize: '20px', color: '#09423F' }}
            ></i>
            <p className="toast-message mb-0" style={{ fontWeight: 500 }}>
              {cloneAttachToast.message}
            </p>
            <Icon
              icon="fal fa-xmark"
              className="toast-close-button"
              onClick={() => setCloneAttachToast(prev => ({ ...prev, isOpen: false }))}
              style={{ fontSize: '16px', color: '#09423F', cursor: 'pointer' }}
              ariaLabel="Close"
            />
          </div>
        </div>
      )}

      {/* VI-651 set-as-default success toast */}
      {setDefaultSuccessToast.isOpen && (
        <div className="toast-notification-container">
          <div className="toast-notification toast-success">
            <i
              className="fal fa-circle-check"
              style={{ fontSize: '20px', color: '#09423F' }}
            ></i>
            <p className="toast-message mb-0" style={{ fontWeight: 500 }}>
              {setDefaultSuccessToast.message}
            </p>
            <Icon
              icon="fal fa-xmark"
              className="toast-close-button"
              onClick={() => setSetDefaultSuccessToast(prev => ({ ...prev, isOpen: false }))}
              style={{ fontSize: '16px', color: '#09423F', cursor: 'pointer' }}
              ariaLabel="Close"
            />
          </div>
        </div>
      )}
    </div>

    
  )
}

export default Home